import { jsPDF } from "jspdf";

const mm = {
  pageWidth: 210,
  margin: 12,
};

const normalizeAmount = (value) => Number(value || 0);

// ── Tunisian tax model (mirrors backend tax_calculator.py) ───────────────────
const CNSS_EMPLOYEE_RATE = 0.0968; // 9,68 %
const CNSS_EMPLOYER_RATE = 0.1707; // 17,07 % (16,07 + 0,4 + 0,6)
// IRPP progressive brackets (annual): [from, to|null, rate]
const IRPP_BRACKETS = [
  [0, 5000, 0.0],
  [5000, 20000, 0.26],
  [20000, 30000, 0.28],
  [30000, 50000, 0.32],
  [50000, null, 0.35],
];

const pct = (rate) => `${(rate * 100).toLocaleString("fr-TN", { maximumFractionDigits: 2 })} %`;

// Per-bracket IRPP breakdown from the annual taxable salary (SAIDE)
const computeIrppBreakdown = (annualTaxable) => {
  const income = normalizeAmount(annualTaxable);
  let total = 0;
  const rows = IRPP_BRACKETS.map(([from, to, rate]) => {
    const upper = to == null ? income : Math.min(income, to);
    const base = Math.max(upper - from, 0);
    const tax = base * rate;
    total += tax;
    return { from, to, rate, base, tax };
  });
  return { rows: rows.filter((r) => r.base > 0), total };
};

// Map a deduction line to its displayed rate
const deductionRateLabel = (item) => {
  const code = String(item?.code || "").toUpperCase();
  if (code === "CNSS_EMPLOYEE") return pct(CNSS_EMPLOYEE_RATE);
  if (code === "TAX_IRPP" || code === "TAX_CNSS") return "Barème";
  return "";
};

const infoAmountByCode = (entry, code) => {
  const item = (entry?.line_items || []).find(
    (l) => String(l.code || "").toUpperCase() === code
  );
  return item ? normalizeAmount(item.amount) : 0;
};

const sanitizePdfText = (value) =>
  String(value ?? "")
    .replace(/\u202f/g, " ")
    .replace(/\u00a0/g, " ");

const amountText = (formatCurrency, value) => sanitizePdfText(formatCurrency(Math.abs(normalizeAmount(value))));

const getLineItems = (entry, category) =>
  (entry?.line_items || [])
    .filter((item) => String(item.category || "").toUpperCase() === category)
    .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0));

const addText = (pdf, text, x, y, options) => {
  pdf.text(sanitizePdfText(text), x, y, options);
};

const addRow = (pdf, y, cols, options = {}) => {
  const { bold = false, fill = false } = options;
  if (fill) {
    pdf.setFillColor(242, 244, 247);
    pdf.rect(mm.margin, y - 5, mm.pageWidth - mm.margin * 2, 8, "F");
  }
  pdf.setFont("helvetica", bold ? "bold" : "normal");
  pdf.setFontSize(8.5);
  cols.forEach((col) => addText(pdf, col.text, col.x, y, col.options));
};

const addSectionTitle = (pdf, title, y) => {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  pdf.setFillColor(230, 234, 242);
  pdf.rect(mm.margin, y - 5, mm.pageWidth - mm.margin * 2, 8, "F");
  addText(pdf, title, mm.margin + 2, y);
};

const splitName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: fullName, lastName: "" };
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
};

const buildFallbackEarnings = (entry) => [
  { label: "Salaire de base", amount: entry.base_pay || entry.base_salary || 0, quantity: null, rate: null },
  { label: "Prime de présence", amount: findAllowanceAmount(entry, /présence|presence|assiduité|attendance/i), quantity: null, rate: null },
  { label: "Prime heures supplémentaires", amount: entry.overtime_pay || 0, quantity: entry.overtime_hours || null, rate: null },
  { label: "Prime de nuit", amount: entry.night_differential || 0, quantity: entry.night_hours || null, rate: null },
  { label: "Prime weekend", amount: entry.weekend_premium || 0, quantity: entry.weekend_hours || null, rate: null },
  { label: "Prime jours fériés", amount: entry.holiday_premium || 0, quantity: entry.holiday_hours || null, rate: null },
].filter((line) => normalizeAmount(line.amount) !== 0 || ["Salaire de base", "Prime de présence"].includes(line.label));

function findAllowanceAmount(entry, matcher) {
  const allowance = (entry?.fixed_allowances || []).find((item) =>
    matcher.test(`${item.type || ""} ${item.description || ""} ${item.label || ""}`)
  );
  return allowance?.amount || 0;
}

const buildFallbackDeductions = (entry) => [
  { label: "Retenue absence", amount: -(entry.absence_deduction || 0), quantity: entry.absence_days_total || null, rate: null },
  { label: "Retenue retard", amount: -(entry.lateness_deduction || 0), quantity: entry.late_minutes_after_tolerance || null, rate: null },
  { label: "Autres retenues", amount: -(entry.other_deductions || 0), quantity: null, rate: null },
].filter((line) => normalizeAmount(line.amount) !== 0);

export const generateTunisiaPayslipPdf = ({
  entries,
  companyInfo,
  selectedPeriod,
  formatCurrency,
  t,
}) => {
  const pdf = new jsPDF("p", "mm", "a4");
  const periodLabel = selectedPeriod
    ? `${selectedPeriod.start_date} - ${selectedPeriod.end_date}`
    : new Date().toISOString().split("T")[0];

  entries.forEach((entry, index) => {
    if (index > 0) pdf.addPage();
    const { firstName, lastName } = splitName(entry.employee_full_name);
    const earnings = getLineItems(entry, "EARNING");
    const deductions = getLineItems(entry, "DEDUCTION");
    const information = getLineItems(entry, "INFORMATION");
    const earningLines = earnings.length > 0 ? earnings : buildFallbackEarnings(entry);
    const deductionLines = deductions.length > 0 ? deductions : buildFallbackDeductions(entry);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    addText(pdf, companyInfo?.name || t("payslipPdf.companyNameFallback"), 105, 14, { align: "center" });
    pdf.setFontSize(12);
    addText(pdf, "BULLETIN DE PAIE - TUNISIE", 105, 22, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    addText(pdf, `Période: ${periodLabel}`, 105, 29, { align: "center" });
    addText(pdf, `Date d'édition: ${new Date().toLocaleDateString("fr-TN")}`, 105, 35, { align: "center" });

    pdf.setDrawColor(50);
    pdf.line(mm.margin, 40, mm.pageWidth - mm.margin, 40);

    addSectionTitle(pdf, "IDENTIFICATION EMPLOYEUR", 49);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    // Two-column rows for the short identifiers…
    addText(pdf, `Raison sociale: ${companyInfo?.legalName || companyInfo?.name || "N/D"}`, 14, 59);
    addText(pdf, `Matricule fiscal: ${companyInfo?.taxIdentifier || companyInfo?.registration || "N/D"}`, 112, 59);
    addText(pdf, `N° CNSS employeur: ${companyInfo?.cnssNumber || "N/D"}`, 14, 66);
    // …and the address on its own full-width line so a long address never
    // overlaps the right column (was colliding with "N° CNSS employeur").
    const addressText = `Adresse: ${companyInfo?.address || "N/D"}`;
    const addressLines = pdf.splitTextToSize(sanitizePdfText(addressText), mm.pageWidth - mm.margin * 2 - 2);
    addText(pdf, addressLines.length > 1 ? `${addressLines[0]} …` : addressLines[0], 14, 73);

    addSectionTitle(pdf, "IDENTIFICATION SALARIE", 79);
    addText(pdf, `Nom: ${lastName || entry.employee_full_name || "N/D"}`, 14, 89);
    addText(pdf, `Prénom: ${firstName || "N/D"}`, 14, 96);
    addText(pdf, `Matricule: ${entry.employee_registration_number || "N/D"}`, 112, 89);
    addText(pdf, `N° CNSS salarié: ${entry.employee_cnss_number || "N/D"}`, 112, 96);
    addText(pdf, `Poste: ${entry.employee_job_title || "N/D"}`, 14, 103);
    addText(pdf, `Département: ${entry.employee_department || "N/D"}`, 112, 103);

    addSectionTitle(pdf, "TEMPS DE TRAVAIL ET ABSENCES", 116);
    addRow(pdf, 126, [
      { text: `Heures prévues: ${entry.expected_hours || 0}h`, x: 14 },
      { text: `Heures travaillées: ${entry.total_worked_hours || 0}h`, x: 62 },
      { text: `Heures sup.: ${entry.overtime_hours || 0}h`, x: 112 },
      { text: `Absences: ${entry.absence_days_total || 0}j`, x: 158 },
    ]);
    addRow(pdf, 134, [
      { text: `Retard: ${entry.late_minutes_after_tolerance || 0} min`, x: 14 },
      { text: `Congé payé: ${entry.paid_leave_hours || 0}h`, x: 62 },
      { text: `Maladie: ${entry.sick_leave_hours || 0}h`, x: 112 },
      { text: `Non payé: ${entry.unpaid_leave_hours || 0}h`, x: 158 },
    ]);

    addSectionTitle(pdf, "RUBRIQUES DE PAIE", 148);
    addRow(pdf, 158, [
      { text: "Gains", x: 14 },
      { text: "Qté", x: 83, options: { align: "right" } },
      { text: "Montant", x: 104, options: { align: "right" } },
      { text: "Retenues", x: 114 },
      { text: "Taux", x: 168, options: { align: "right" } },
      { text: "Montant", x: 195, options: { align: "right" } },
    ], { bold: true, fill: true });

    const maxRows = Math.max(earningLines.length, deductionLines.length, 1);
    let y = 166;
    for (let i = 0; i < maxRows; i += 1) {
      const earning = earningLines[i];
      const deduction = deductionLines[i];
      addRow(pdf, y, [
        { text: earning?.label || "", x: 14 },
        { text: earning?.quantity ? String(earning.quantity) : "", x: 83, options: { align: "right" } },
        { text: earning ? amountText(formatCurrency, earning.amount) : "", x: 104, options: { align: "right" } },
        { text: deduction?.label || "", x: 114 },
        { text: deduction ? deductionRateLabel(deduction) : "", x: 168, options: { align: "right" } },
        { text: deduction ? amountText(formatCurrency, deduction.amount) : "", x: 195, options: { align: "right" } },
      ]);
      y += 8;
    }

    const saide = infoAmountByCode(entry, "INFO_SAIDE");
    const irppMonthly = Math.abs(infoAmountByCode(entry, "TAX_IRPP"));

    y += 4;
    addSectionTitle(pdf, "BASES ET INFORMATIONS SOCIALES/FISCALES", y);
    y += 10;
    // When the IRPP detail is shown below, keep BASES compact to avoid
    // repeating SAIDE / CNSS employeur (covered by the dedicated sections).
    const infoLines = saide > 0
      ? [{ label: "Assiette CNSS (base cotisations)", amount: entry.gross_pay || 0 }]
      : (information.length > 0 ? information : [
          { label: "Assiette CNSS", amount: entry.gross_pay || 0 },
          { label: "Net imposable / base IRPP", amount: Math.max(normalizeAmount(entry.gross_pay) - normalizeAmount(entry.total_deductions), 0) },
        ]);
    infoLines.slice(0, 5).forEach((line) => {
      addRow(pdf, y, [
        { text: line.label, x: 14 },
        { text: amountText(formatCurrency, line.amount), x: 104, options: { align: "right" } },
      ]);
      y += 7;
    });

    // ── DÉTAIL DU CALCUL IRPP (barème progressif) ──────────────────────────
    if (saide > 0) {
      y += 3;
      addSectionTitle(pdf, "DETAIL DU CALCUL IRPP (bareme progressif)", y);
      y += 8;
      addText(pdf, `Salaire annuel imposable (SAIDE): ${amountText(formatCurrency, saide)}`, 14, y);
      y += 6;
      addRow(pdf, y, [
        { text: "Tranche annuelle", x: 14 },
        { text: "Taux", x: 104, options: { align: "right" } },
        { text: "Base imposable", x: 150, options: { align: "right" } },
        { text: "Impôt", x: 195, options: { align: "right" } },
      ], { bold: true });
      y += 5;
      const irpp = computeIrppBreakdown(saide);
      irpp.rows.forEach((r) => {
        const range = r.to == null
          ? `> ${r.from.toLocaleString("fr-TN")}`
          : `${r.from.toLocaleString("fr-TN")} – ${r.to.toLocaleString("fr-TN")}`;
        addRow(pdf, y, [
          { text: range, x: 14 },
          { text: pct(r.rate), x: 104, options: { align: "right" } },
          { text: amountText(formatCurrency, r.base), x: 150, options: { align: "right" } },
          { text: amountText(formatCurrency, r.tax), x: 195, options: { align: "right" } },
        ]);
        y += 5;
      });
      addRow(pdf, y, [
        { text: "IRPP annuel", x: 14 },
        { text: amountText(formatCurrency, irpp.total), x: 150, options: { align: "right" } },
        { text: `Mensuel: ${amountText(formatCurrency, irppMonthly || irpp.total / 12)}`, x: 195, options: { align: "right" } },
      ], { bold: true });
      y += 7;
    }

    // ── CHARGES PATRONALES ─────────────────────────────────────────────────
    const cnssEmployer = infoAmountByCode(entry, "INFO_CNSS_EMPLOYER");
    if (cnssEmployer > 0) {
      addRow(pdf, y, [
        { text: `Charges patronales CNSS (${pct(CNSS_EMPLOYER_RATE)})`, x: 14 },
        { text: amountText(formatCurrency, cnssEmployer), x: 104, options: { align: "right" } },
        { text: "(employeur)", x: 150 },
      ]);
      y += 7;
    }

    // Safety: keep the totals + footer on the page even for high earners
    if (y > 270) {
      pdf.addPage();
      y = 24;
    }
    y = y + 4;
    pdf.setDrawColor(80);
    pdf.line(mm.margin, y - 6, mm.pageWidth - mm.margin, y - 6);
    addRow(pdf, y, [
      { text: "Salaire brut", x: 14 },
      { text: amountText(formatCurrency, entry.gross_pay), x: 104, options: { align: "right" } },
      { text: "Total retenues salariales", x: 114 },
      { text: amountText(formatCurrency, entry.total_deductions), x: 195, options: { align: "right" } },
    ], { bold: true });
    addRow(pdf, y + 11, [
      { text: "NET A PAYER", x: 14 },
      { text: amountText(formatCurrency, entry.net_pay), x: 195, options: { align: "right" } },
    ], { bold: true, fill: true });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    addText(pdf, "Document à conserver sans limitation de durée. Les montants sont exprimés en dinar tunisien.", 105, 291, { align: "center" });
    addText(pdf, "Les champs non renseignés dans le dossier salarié sont affichés N/D.", 105, 295, { align: "center" });
  });

  return pdf;
};
